import React, { useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import {
  Field,
  InjectedFormProps,
  reduxForm,
  formValueSelector,
} from "redux-form";
import { useLocation } from "react-router-dom";
import {
  IonCard,
  IonCardContent,
  IonGrid,
  IonRow,
  IonCol,
  IonButton,
  IonIcon,
  IonCardHeader,
  IonCardTitle,
  IonLabel,
  IonText,
} from "@ionic/react";
import { caretBack, caretForward } from "ionicons/icons";

import { RootState } from "../../store";
import {
  getList,
  clearList,
} from "../../store/administrative/assistances/actions";
import {
  Assistance,
  AssistanceForm,
} from "../../store/administrative/assistances/types";

import Main from "../../components/Main";
import HospitalSearch from "../../components/Legacy/Search/Hospital";
import DateInput from "../../components/Legacy/Form/DateInput";
import If from "../../helpers/Legacy/if";
import dateToString from "../../helpers/Legacy/dateToString";

interface StateProps {
  list: Assistance[];
  formSelector: AssistanceForm;
}

interface DispatchProps {
  getList(values: AssistanceForm): void;
  clearList(): void;
}

type Props = StateProps & DispatchProps;

const AdministrativeAssistances: React.FC<
  Props & InjectedFormProps<AssistanceForm, Props>
> = ({ handleSubmit, getList, clearList, list, formSelector, change }) => {
  const location = useLocation();

  useEffect(() => {
    clearList();
  }, [location, clearList]);

  function handlePrevious() {
    let yesterday = new Date(formSelector.date);
    yesterday.setDate(yesterday.getDate() - 1);
    change("date", yesterday.toDateString());
    formSelector.date = yesterday.toDateString();
    getList(formSelector);
  }

  function handleNext() {
    let tomorrow = new Date(formSelector.date);
    tomorrow.setDate(tomorrow.getDate() + 1);
    change("date", tomorrow.toDateString());
    formSelector.date = tomorrow.toDateString();
    getList(formSelector);
  }

  return (
    <Main title="Atendimentos">
      <IonCard>
        <IonCardContent>
          <form onSubmit={handleSubmit(getList)}>
            <IonGrid>
              <IonRow>
                <Field name="hospital" component={HospitalSearch} required />
                <Field name="date" component={DateInput} label="Data" />
              </IonRow>
              <IonRow className="ion-margin-top">
                <IonCol size="3">
                  <IonButton
                    type="button"
                    onClick={handlePrevious}
                    color="primary"
                    expand="block"
                    disabled={!!formSelector.date ? false : true}
                  >
                    <IonIcon size="small" icon={caretBack} />
                  </IonButton>
                </IonCol>
                <IonCol size="6">
                  <IonButton type="submit" color="primary" expand="block">
                    Consultar
                  </IonButton>
                </IonCol>
                <IonCol size="3">
                  <IonButton
                    type="button"
                    onClick={handleNext}
                    color="primary"
                    expand="block"
                    disabled={!!formSelector.date ? false : true}
                  >
                    <IonIcon size="small" icon={caretForward} />
                  </IonButton>
                </IonCol>
              </IonRow>
            </IonGrid>
          </form>
        </IonCardContent>
      </IonCard>
      {list.map((values) => {
        return (
          <IonCard
            key={values.id}
            className={`card-${values.situation?.toLowerCase()}`}
          >
            <IonCardHeader>
              <IonCardTitle>Situação: {values.situation}</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonRow>
                <IonCol size="12">
                  <IonLabel>Id: </IonLabel>
                  <IonText>{values.id}</IonText>
                </IonCol>
                <IonCol size="12">
                  <IonLabel>Data: </IonLabel>
                  <IonText>{dateToString(values.date)}</IonText>
                </IonCol>
                <IonCol size="12">
                  <IonLabel>Hospital: </IonLabel>
                  <IonText>{values.hospital}</IonText>
                </IonCol>
                <IonCol size="12">
                  <IonLabel>Paciente: </IonLabel>
                  <IonText>{values.patient}</IonText>
                </IonCol>
                <IonCol size="12">
                  <IonLabel>Novo: </IonLabel>
                  <IonText>
                    {values.new_patient === "S" ? "Sim" : "Não"}
                  </IonText>
                </IonCol>
                <IonCol size="12">
                  <IonLabel>Localização: </IonLabel>
                  <IonText>{values.location}</IonText>
                </IonCol>
                <IonCol size="12">
                  <IonLabel>Leito: </IonLabel>
                  <IonText>{values.bed}</IonText>
                </IonCol>
                <If test={values.technician1 ? true : false}>
                  <IonCol size="12">
                    <IonLabel>Técnico 1: </IonLabel>
                    <IonText>{values.technician1}</IonText>
                  </IonCol>
                </If>
                <If test={values.technician2 ? true : false}>
                  <IonCol size="12">
                    <IonLabel>Técnico 2: </IonLabel>
                    <IonText>{values.technician2}</IonText>
                  </IonCol>
                </If>
                <If test={values.technician3 ? true : false}>
                  <IonCol size="12">
                    <IonLabel>Técnico 3: </IonLabel>
                    <IonText>{values.technician3}</IonText>
                  </IonCol>
                </If>
                <IonCol size="12">
                  <IonLabel>Destino: </IonLabel>
                  <IonText>{values.destination}</IonText>
                </IonCol>
                <IonCol size="12">
                  <IonLabel>Evento: </IonLabel>
                  <IonText>{values.event}</IonText>
                </IonCol>
                <IonCol size="12">
                  <IonLabel>Procedimento: </IonLabel>
                  <IonText>{values.proc}</IonText>
                </IonCol>
                <IonCol size="12">
                  <IonLabel>Tempo (Horas): </IonLabel>
                  <IonText>{values.time}</IonText>
                </IonCol>
                <IonCol size="12">
                  <IonLabel>Início: </IonLabel>
                  <IonText>{values.start}</IonText>
                </IonCol>
                <IonCol size="12">
                  <IonLabel>Fim: </IonLabel>
                  <IonText>{values.end}</IonText>
                </IonCol>
                <IonCol size="12">
                  <IonLabel>Simultâneo: </IonLabel>
                  <IonText>{values.simultaneous_id}</IonText>
                </IonCol>
                <IonCol size="12">
                  <IonLabel>Acesso: </IonLabel>
                  <IonText>{values.access}</IonText>
                </IonCol>
                <IonCol size="12">
                  <IonLabel>Sitio: </IonLabel>
                  <IonText>{values.site}</IonText>
                </IonCol>
                <IonCol size="12">
                  <IonLabel>Maq.: </IonLabel>
                  <IonText>{values.machine}</IonText>
                </IonCol>
                <IonCol size="12">
                  <IonLabel>OR: </IonLabel>
                  <IonText>{values.osmosis}</IonText>
                </IonCol>
                <IonCol size="12">
                  <IonLabel>Home Choice: </IonLabel>
                  <IonText>{values.home_choice}</IonText>
                </IonCol>
                <IonCol size="12">
                  <IonLabel>Médico: </IonLabel>
                  <IonText>{values.doctor}</IonText>
                </IonCol>
                <IonCol size="12">
                  <IonLabel>Convênio: </IonLabel>
                  <IonText>{values.agreement}</IonText>
                </IonCol>
                <IonCol size="12">
                  <IonLabel>Matrícula: </IonLabel>
                  <IonText>{values.registration}</IonText>
                </IonCol>
                <IonCol size="12">
                  <IonLabel>Observação: </IonLabel>
                  <IonText>{values.note}</IonText>
                </IonCol>
                <IonCol size="12">
                  <IonLabel>Válidado: </IonLabel>
                  <IonText>{values.validated === "S" ? "Sim" : "Não"}</IonText>
                </IonCol>
                <IonCol size="12">
                  <IonLabel>Folha: </IonLabel>
                  <IonText>{values.report}</IonText>
                </IonCol>
              </IonRow>
            </IonCardContent>
          </IonCard>
        );
      })}
    </Main>
  );
};

const sessionKey = `${process.env.REACT_APP_SESSION_NAME}`;

const AdministrativeAssistancesForm = reduxForm<AssistanceForm, Props>({
  form: "administrative_assistances",
  initialValues: JSON.parse(
    `${localStorage.getItem(`${sessionKey}_administrative_assistances`)}`
  ),
})(AdministrativeAssistances);

const selector = formValueSelector("administrative_assistances");

const mapStateToProps = (state: RootState) => ({
  list: state.administrative_assistance.list,
  formSelector: selector(state, "hospital", "date"),
});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators({ getList, clearList }, dispatch);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AdministrativeAssistancesForm);
